import type { Tx } from '../../domain/tx';
import type { EntityMap, EntityName, Id } from '../../domain/types';
import type { Tables } from '../types';
import { BaseStorage, emptyTables, type Change, type Executed } from './base';
import { applyFilter } from './query';

type Table = Map<Id, unknown>;
const clone = <T>(v: T): T => structuredClone(v);

/**
 * In-memory storage with the same semantics as IndexedDB: transactions are serialised, isolated
 * (copy-on-write) and rolled back when the callback throws; rows are cloned in and out.
 */
export class MemoryStorage extends BaseStorage {
  readonly kind = 'memory' as const;
  private tables = new Map<EntityName, Table>();
  private queue: Promise<unknown> = Promise.resolve();

  constructor(opts: { channelName?: string } = {}) {
    super(opts.channelName);
  }

  private table(entity: EntityName, source = this.tables): Table {
    let t = source.get(entity);
    if (!t) { t = new Map(); source.set(entity, t); }
    return t;
  }

  protected execute<R>(_entities: readonly EntityName[], mode: 'r' | 'rw', fn: (tx: Tx) => Promise<R>): Promise<Executed<R>> {
    const run = async (): Promise<Executed<R>> => {
      const working = new Map<EntityName, Table>();
      const changes: Change[] = [];
      const wt = (entity: EntityName): Table => {
        let t = working.get(entity);
        if (!t) { t = new Map(this.table(entity)); working.set(entity, t); }
        return t;
      };
      const tx: Tx = {
        get: async (entity, id) => { const r = wt(entity).get(id); return r === undefined ? undefined : clone(r) as never; },
        list: async (entity, filter) => applyFilter([...wt(entity).values()] as EntityMap[typeof entity][], filter).map(clone) as never,
        put: async (entity, row) => {
          if (mode === 'r') throw new Error('read-only transaction');
          wt(entity).set(row.id, clone(row));
          changes.push({ entity, type: 'put', id: row.id });
          return row;
        },
        delete: async (entity, id) => {
          if (mode === 'r') throw new Error('read-only transaction');
          wt(entity).delete(id);
          changes.push({ entity, type: 'delete', id });
        },
      };
      const value = await fn(tx);
      for (const [entity, t] of working) this.tables.set(entity, t);
      return { value, changes };
    };
    const next = this.queue.then(run, run);
    this.queue = next.catch(() => undefined);
    return next;
  }

  protected async replaceAll(tables: Tables): Promise<void> {
    await this.queue;
    this.tables = new Map();
    for (const [entity, rows] of Object.entries(tables) as Array<[EntityName, Array<{ id: Id }>]>) {
      const t = this.table(entity);
      for (const r of rows) t.set(r.id, clone(r));
    }
  }

  protected async dumpAll(): Promise<Tables> {
    await this.queue;
    const out = emptyTables() as unknown as Record<EntityName, unknown[]>;
    for (const [entity, t] of this.tables) out[entity] = [...t.values()].map(clone);
    return out as unknown as Tables;
  }

  protected closeImpl(): void { /* nothing to release */ }
}

export function createMemoryStorage(opts: { channelName?: string } = {}): MemoryStorage {
  return new MemoryStorage(opts);
}
